import type { Links, Meta } from '@library/types/utils';

export type HierarchicalRulesetBaseAttributes = {
  name: string;
  description: string;
  active: boolean;
  level_scope_type: LevelScopeType;
  level_scope_id: number;
  hierarchy_type: HierarchyType;
  qualified_path?: string;
};

/** Detailed data about a ruleset including all rule values but does not include children. */
export type HierarchicalRuleset = {
  id: string;
  type: string;
  attributes: HierarchicalRulesetBaseAttributes & {
    // qualified_path?: string;
    hierarchy_type: HierarchyType;
    max_lineup_amount: NumberField;
    max_amount_won: NumberField;
    max_daily_lineup_amount: NumberField;
    max_liability_per_pick: NumberField;
    flash_promo_max: NumberField;
    daily_promo_limit: NumberField;
    min_lineup_amount: NumberField;
    in_game_multiplier: NumberField;
    refund_in_minutes: NumberField;
    allow_tailed_entries: BooleanField;
    allow_two_players_from_same_game: BooleanField;
    allow_two_players_from_same_team: BooleanField;
    allow_deposits: BooleanField;
    enforce_at_least_two_players_from_two_different_games: BooleanField;
    enforce_at_least_two_players_from_two_different_teams: BooleanField;
    enforce_single_game_validations: BooleanField;
    allow_withdrawals: BooleanField;
  };
  relationships: {
    parent: {
      data: { id: string; type: string } | null;
    };
  };
};

/** Sparse tree metadata about a ruleset and its children. Doesn't include any data fields about the ruleset which must be fetched separately. */
export interface HierarchicalRulesetMetadata {
  id: HierarchicalRuleset['id'];
  type: HierarchicalRuleset['type'];
  attributes: HierarchicalRulesetBaseAttributes & {
    // qualified_path?: string;
    children: {
      data: Array<HierarchicalRulesetMetadata>;
    };
  };
}

export interface AncestorRuleset {
  type: ValueType;
  value: number | boolean;
  name: string;
  id: number;
  derived: number;
}

export type HierarchyType =
  | 'GameOps'
  | 'Geography'
  | 'GameMode'
  | 'UserGroup'
  | 'User';

export type LevelScopeType =
  | 'GameOps'
  | 'GameMode'
  | 'GameType'
  | 'Sport'
  | 'League'
  | 'StatType'
  | 'Geography'
  | 'State'
  | 'PlayerCount'
  | 'UserGroup'
  | 'User';

export type ValueType = 'literal' | 'multiplier' | 'boolean';

export type TypedField = { type: ValueType; value: number | boolean };

export type BooleanField = {
  type: ValueType;
  value: boolean;
  ancestor: AncestorRuleset | null;
};

export type ModifiedBooleanField =
  | {
      type: ValueType;
      value: boolean;
    }
  | undefined;

export type NumberField = {
  type: ValueType;
  value?: number;
  ancestor: AncestorRuleset | null;
};

export type ModifiedNumberField =
  | {
      type: ValueType;
      value: number;
    }
  | undefined;

export type GetHierarchicalRulesetsResponse = {
  data: Array<HierarchicalRulesetMetadata>;
};

export interface CreateHierarchicalRulesetRequest {
  name: string;
  description?: string;
  active: boolean;
  parent_id?: number | null; // TODO: Verify null when creating base-level ruleset
  level_scope_type: LevelScopeType;
  level_scope_id: number;
  qualified_path?: string;
  max_lineup_amount?: {
    type: ValueType;
    value: number;
  };
  max_amount_won?: {
    type: ValueType;
    value: number;
  };
  max_daily_lineup_amount?: {
    type: ValueType;
    value: number;
  };
  max_liability_per_pick?: {
    type: ValueType;
    value: number;
  };
  flash_promo_max?: {
    type: ValueType;
    value: number;
  };
  daily_promo_limit?: {
    type: ValueType;
    value: number;
  };
  min_lineup_amount?: {
    type: ValueType;
    value: number;
  };
  refund_in_minutes?: {
    type: ValueType;
    value: number;
  };
  in_game_multiplier?: {
    type: ValueType;
    value: number;
  };
  allow_tailed_entries?: {
    type: ValueType;
    value: boolean;
  };
  allow_two_players_from_same_game?: {
    type: ValueType;
    value: boolean;
  };
  allow_two_players_from_same_team?: {
    type: ValueType;
    value: boolean;
  };
  allow_deposits?: {
    type: ValueType;
    value: boolean;
  };
  enforce_at_least_two_players_from_two_different_games?: {
    type: ValueType;
    value: boolean;
  };
  enforce_at_least_two_players_from_two_different_teams?: {
    type: ValueType;
    value: boolean;
  };
  enforce_single_game_validations?: {
    type: ValueType;
    value: boolean;
  };
  allow_withdrawals?: {
    type: ValueType;
    value: boolean;
  };
}

export interface CreateHierarchicalRulesetResponse {
  data: HierarchicalRuleset;
}

export type GetHierarchicalRulesetResponse = {
  data: HierarchicalRuleset | Array<HierarchicalRuleset>;
};

export type UpdateHierarchicalRulesetResponse =
  CreateHierarchicalRulesetResponse;

export type UpdateHierarchicalRulesetRequest = ModifiedFields;

export interface GetAllowedRulesResponse {
  data: {
    id: string;
    type: string;
    attributes: {
      allowed_rules: Record<
        HierarchyType,
        Record<LevelScopeType, Array<HierarchicalRulesetConfigField>>
      >;
    };
  };
}

export interface GetAllowedRuleTypesResponse {
  data: {
    id: string;
    type: string;
    attributes: {
      allowed_rule_types: Record<
        HierarchyType,
        Record<
          LevelScopeType,
          Partial<Record<HierarchicalRulesetConfigField, Array<ValueType>>>
        >
      >;
    };
  };
}

export interface PreviewChangesIndividualRuleset {
  id: string;
  type: string;
  attributes: {
    name: string;
    description: string;
    active: boolean;
    level_scope_type: LevelScopeType;
    level_scope_id: number;
    affected_node_count: number;
    max_lineup_amount: BooleanPreviewField;
    max_amount_won: NumberPreviewField;
    max_daily_lineup_amount: NumberPreviewField;
    max_liability_per_pick: NumberPreviewField;
    flash_promo_max: NumberPreviewField;
    daily_promo_limit: NumberPreviewField;
    min_lineup_amount: NumberPreviewField;
    in_game_multiplier: NumberPreviewField;
    refund_in_minutes: NumberPreviewField;
    allow_tailed_entries: BooleanPreviewField;
    allow_two_players_from_same_game: BooleanPreviewField;
    allow_two_players_from_same_team: BooleanPreviewField;
    allow_deposits: BooleanPreviewField;
    enforce_at_least_two_players_from_two_different_games: BooleanPreviewField;
    enforce_at_least_two_players_from_two_different_teams: BooleanPreviewField;
    enforce_single_game_validations: BooleanPreviewField;
    allow_withdrawals: BooleanPreviewField;
    children: {
      data: Array<PreviewChangesIndividualRuleset>;
    };
  };
}

export interface PreviewChangesResponse {
  data: PreviewChangesIndividualRuleset;
}

export const RULESETS_METADATA_FIELDS = [
  'name',
  'description',
  'active'
] as const;
type RulesetsMetadataTuple = typeof RULESETS_METADATA_FIELDS;
export type HierarchicalRulesetMetadataField = RulesetsMetadataTuple[number];

export const RULESETS_CONFIG_FIELDS = [
  'max_lineup_amount',
  'max_amount_won',
  'max_daily_lineup_amount',
  'max_liability_per_pick',
  'flash_promo_max',
  'daily_promo_limit',
  'min_lineup_amount',
  'refund_in_minutes',
  'in_game_multiplier',
  'allow_tailed_entries',
  'allow_two_players_from_same_game',
  'allow_two_players_from_same_team',
  'allow_deposits',
  'enforce_at_least_two_players_from_two_different_games',
  'enforce_at_least_two_players_from_two_different_teams',
  'enforce_single_game_validations',
  'allow_withdrawals'
] as const;
type RulesetsConfigTuple = typeof RULESETS_CONFIG_FIELDS;
export type HierarchicalRulesetConfigField = RulesetsConfigTuple[number];

export interface RulesetsGlobalContext {
  setExpandAllPhantoms: React.Dispatch<React.SetStateAction<boolean>>;
  selectedQualifiedPath: string;
  setSelectedQualifiedPath: React.Dispatch<React.SetStateAction<string>>;
  currentRuleset: HierarchicalRuleset | undefined;
  expandAllPhantoms: boolean;
  gameOpsGlobalRuleset: HierarchicalRulesetMetadata | undefined;
  gameModesGlobalRulesets: Array<HierarchicalRulesetMetadata> | undefined;
  modifiedFields: ModifiedFields;
  handleDiscardChanges: () => void;
  handleUpdateRulesetField: ({
    fieldName,
    type,
    value
  }: {
    fieldName:
      | HierarchicalRulesetMetadataField
      | HierarchicalRulesetConfigField;
    type?: ValueType;
    value: string | number | boolean;
  }) => void;
  handleSubmitUpdate: () => Promise<void>;
  handleResetRuleset: () => void;
  isSubmitSuccess: boolean;
  isSubmitError: boolean;
  isSubmitLoading: boolean;
  resetUpdate: () => void;
  resetCreate: () => void;
  isRulesetFetching: boolean;
  isGameOpsLoading: boolean;
  isGameOpsFetching: boolean;
  isGameModesLoading: boolean;
  isGameModesFetching: boolean;
  allowedRules:
    | GetAllowedRulesResponse['data']['attributes']['allowed_rules']
    | undefined;
  matchedAllowedRules: Set<string>;
  matchedAllowedRuleTypes: Partial<
    Record<HierarchicalRulesetConfigField, Array<ValueType>>
  >;
  handleOpenPreviewModal: () => void;
  handleClosePreviewModal: () => void;
  isPreviewModalOpen: boolean;
  handlePreviewChanges: () => Promise<void>;
  isPreviewLoading: boolean;
  previewData: PreviewData | null;
  handleResetPreview: () => void;
  customRuleset?: HierarchicalRulesetMetadata;
  isFetchingCustomRulesetRuleset: boolean;
}

export type ModifiedFields = Partial<
  Omit<
    CreateHierarchicalRulesetRequest,
    'parent_id' | 'level_scope_type' | 'level_scope_id'
  >
>;

export type NumberPreviewField = NumberField & {
  derived: number;
  preview_derived: number;
  preview_value: number;
  preview_type: Omit<ValueType, 'boolean'>;
};

export type BooleanPreviewField = BooleanField & {
  derived: boolean;
  preview_derived: boolean;
  preview_value: boolean;
  preview_type: Omit<ValueType, 'literal' | 'multiplier'>;
};

export interface PreviewDataChildContent {
  rulesetName: string;
  affectedFields: Array<{
    fieldName: string;
    newValue: number | boolean;
    previousValue: number | boolean;
  }>;
}

export interface PreviewData {
  total: number;
  childContent: Array<PreviewDataChildContent>;
}

// Legacy Rulesets
export interface LegacyRulesetsResponse {
  data: LegacyRulesets;
  meta: Meta;
  links: Links;
}

export type LegacyRulesets = Array<LegacyRuleset>;

export interface LegacyRuleset {
  id: string;
  type: string;
  attributes: {
    name: string;
  };
}

export interface LegacyRulesetsRequest {
  omit_users?: boolean;
  page?: number;
  per_page?: number;
}
